import { ApiError } from './api';

/*
 * What an HR page says when a request is refused for a TENANCY reason rather than a business one.
 *
 * Both codes below are answered by requireCompany() before any handler runs, and both carry a
 * server sentence written for an API consumer rather than for a recruiter looking at a page:
 * "Specify which company this request is for." names no control they can see, and "Your account
 * isn't attached to a company yet." is actively confusing on a screen listing that company's own
 * jobs. Every other code keeps its server sentence verbatim — the server is the one that knows
 * why a business rule refused, and second-guessing it here is how error copy drifts out of date.
 *
 * A plain .ts module, not a const inside a .tsx page, for the same reason
 * src/pages/Admin/companyErrorCopy.ts is: vitest.web collects only src/**\/*.test.ts and runs it
 * in a node environment with no JSX transform (vitest.web.config.ts), so copy living in a .tsx
 * file cannot be asserted on at all.
 */

const TENANCY_MESSAGE: Record<string, string> = {
  /*
   * 400 company_required — more than one active membership and the request carried no
   * ?companyId=. With src/lib/api.ts threading the active company onto every call this should be
   * unreachable, so seeing it means the active company is unset or was rejected, and the fix is
   * to pick one. Deliberately does NOT offer a retry: the same request without a choice fails
   * identically, and telling someone to try again is how a dead end gets mistaken for a blip.
   */
  company_required:
    'Choose which company this is for — the company selector sits in the header, next to your name. Repeating this request without choosing gets the same answer.',
  /*
   * 403 no_company — the caller holds no active membership, or named one that is not theirs.
   * Mid-session this means the seat was removed or the employer was suspended while the page was
   * open. Reloading is a real fix rather than a shrug: it re-reads /api/me, and CompanyGate then
   * shows the screen that explains which of the two happened.
   */
  no_company:
    'This account is no longer attached to that company. Reload the page — you may have been removed from it, or the company may have been suspended.',
};

/**
 * The message to show for a failed HR request. Drop-in for the
 * `err instanceof ApiError ? err.message : fallback` this replaces: a non-ApiError still gets
 * `fallback`, and an ApiError with no tenancy meaning still gets the server's own sentence.
 */
export function hrErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  return TENANCY_MESSAGE[err.code] ?? err.message;
}
